/* The rule as a SENTENCE with two of its numbers pressable — the mock's `sentence3` box.
 *
 * The box holds a wrapping row of labelled controls joined by words, and a second row carrying the
 * range switch and the family pill. Every control is `24px` tall so the row reads as one line of
 * type with things you can press inside it, which is the whole idea: the trainer is editing a
 * sentence, not filling a form.
 *
 * ── The family HUE is on the chip and the pill, and nowhere else ────────────────────────────
 * Not on the step, not on the joining words, not on the box. The mock tints the family chip's ink
 * and border and leaves the rest of the row neutral, and that is what keeps four coloured things on
 * this surface rather than fourteen. The accent (orange) appears on neither — it belongs to a pinned
 * cell and the focus ring.
 */

.box {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-6px) var(--space-9px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.row {
  display: flex;
  gap: var(--space-2);
  align-items: flex-end;
  flex-wrap: wrap;
}

.switchRow {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
}

/* A control and the word naming it, stacked. The mock's `fieldCol`. */
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
}

.fieldLabel {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-eyebrow);
  text-transform: uppercase;
  color: var(--text-2);
  white-space: nowrap;
}

/* The joining words. Regular weight on purpose — they are prose between controls, and bolding them
 * would make the row read as a row of labels rather than a sentence. */
.word {
  display: inline-flex;
  align-items: center;
  height: var(--h-prog-chip);
  font-size: var(--fs-note);
  font-weight: var(--fw-regular);
  color: var(--text-2);
}

.wordMuted {
  color: var(--text-2);
}

/* ── The family chip, and the gate column chip beside it ─────────────────────────────────── */

.chip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-prog-chip);
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

.chip:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.chip[data-family='static'] {
  color: var(--prog-sheet-static-ink);
  border-color: var(--prog-sheet-static);
}

.chip[data-family='fromResult'] {
  color: var(--prog-sheet-result-ink);
  border-color: var(--prog-sheet-result);
}

.chipDot {
  display: block;
  flex: none;
  width: var(--space-7px);
  height: var(--space-7px);
  background: var(--text-3);
  border-radius: var(--r-circle);
}

.chipDot[data-family='static'] {
  background: var(--prog-sheet-static);
}

.chipDot[data-family='fromResult'] {
  background: var(--prog-sheet-result);
}

.caret {
  font-size: var(--fs-micro-label);
  font-style: normal;
  opacity: 0.6;
}

/* ── The step stepper ────────────────────────────────────────────────────────────────────── */

/* Its groove, its two ends and its readout are `shared/ui/stepper` — promoted 2 October 2026 on its
 * second consumer, the task kinds' level. */

/* ── The kg / % segment, which the mock does not draw ────────────────────────────────────── */

/* Additive, and the plan's own call: the mock prints kilograms only, while the contract and mobile
 * both allow a COMPOUNDING percent step on the load — and without this control the web cannot
 * author one at all. It sits beside the stepper rather than inside it, because it re-denominates the
 * number rather than changing it. The two share one line under the one PRZYROST label. */
.stepLine {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
}

/* A compact two-option segment on the scenario segment's own tokens (`TrajectoryPreview.module.css`
 * `.segment` / `.option`): two EQUAL tracks sized by the wider option, so the selected pill fills
 * exactly its half and nothing stretches it when the row wraps. */
.unitSegment {
  display: inline-grid;
  flex: none;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: var(--space-3px);
  padding: var(--space-3px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
}

.unitOption {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--h-prog-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
}

.unitOption[aria-checked='true'] {
  color: var(--bg-card);
  background: var(--text);
}

.unitOption:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.unitOption[aria-disabled='true'] {
  cursor: default;
  opacity: 0.5;
}

/* ── The range field ─────────────────────────────────────────────────────────────────────── */

.range {
  width: var(--space-16);
  height: var(--h-prog-chip);
  padding: var(--space-0) var(--space-10px);
  font: inherit;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  text-align: center;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

.range:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.range[aria-invalid='true'] {
  border-color: var(--danger);
}

.rangeRefusal {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  color: var(--danger-ink);
}

/* What the next press COSTS — the rule on the column the band is about to take over. Drawn whether
 * or not the `?` is on, because it is not a hint. The gate menu states the same thing on the entry
 * that would cost it. */
.warning {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  color: var(--danger-ink);
}

/* ── The range switch ────────────────────────────────────────────────────────────────────── */

.bind {
  display: inline-flex;
  gap: var(--space-7px);
  align-items: center;
  padding: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: none;
}

.bind:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-xs);
}

.bind[aria-disabled='true'] {
  cursor: default;
  color: var(--text-2);
}

.track {
  position: relative;
  display: block;
  flex: none;
  width: var(--space-8);
  height: var(--space-5);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-mark);
  transition: background var(--dur-fast) var(--ease-default);
}

.bind[aria-checked='true'] .track {
  background: var(--text);
  border-color: var(--text);
}

.knob {
  position: absolute;
  inset-block-start: var(--space-2px);
  inset-inline-start: var(--space-2px);
  width: var(--space-3);
  height: var(--space-3);
  background: var(--text-3);
  border-radius: var(--r-circle);
  transition: translate var(--dur-fast) var(--ease-default);
}

.bind[aria-checked='true'] .knob {
  background: var(--bg-card);
  translate: var(--space-13px) 0;
}

@media (prefers-reduced-motion: reduce) {
  .track,
  .knob {
    transition: none;
  }
}

/* ── The family pill, under the `?` ──────────────────────────────────────────────────────── */

.familyPill {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: var(--space-22px);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
}

.familyPill[data-family='static'] {
  color: var(--prog-sheet-static-ink);
}

.familyPill[data-family='fromResult'] {
  color: var(--prog-sheet-result-ink);
}

.spring {
  flex: 1;
}

/* ── The menus' own rows ─────────────────────────────────────────────────────────────────── */

/* A menu entry that carries a second line — the family's own note, or the refusal that greys a gate
 * column out. `MenuSurface` supplies the popup; this is only what a two-line item does inside it. */
.menuEntry {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.menuNote {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* „Resetuj progresję" at the foot of the family menu: an ACT, drawn as a red outline button rather
 * than as a fourth radio entry (owner, 15 September 2026). The danger edge and ink with no fill,
 * the same pair `ConfirmDialog`'s destructive ghost wears, and no shadow of any colour. */
.resetAction {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: var(--space-1) var(--space-2) var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--danger-text-strong);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--danger);
  border-radius: var(--r-button);
  outline: none;
}

.resetAction[data-highlighted] {
  background: var(--danger-soft);
}
